import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, Users, DollarSign, ArrowUpRight, ArrowDownRight, FileText, Gamepad2, ShieldCheck, Loader2, CornerDownLeft } from "lucide-react";
import api from "../../lib/api";

// Page shortcuts — matched by keyword
const PAGE_SHORTCUTS = [
  { keywords: ["dashboard", "home", "overview"], label: "Dashboard", path: "/root/admin/dashboard", icon: Gamepad2 },
  { keywords: ["player", "players", "user", "users"], label: "Players", path: "/root/admin/players", icon: Users },
  { keywords: ["deposit", "deposits"], label: "All Deposits", path: "/root/admin/deposits/all", icon: ArrowDownRight },
  { keywords: ["pending deposit", "deposits pending"], label: "Pending Deposits", path: "/root/admin/deposits/pending", icon: ArrowDownRight },
  { keywords: ["withdraw", "withdrawal", "withdrawals", "withdraws"], label: "All Withdrawals", path: "/root/admin/withdraws/all", icon: ArrowUpRight },
  { keywords: ["pending withdraw", "withdraws pending"], label: "Pending Withdrawals", path: "/root/admin/withdraws/pending", icon: ArrowUpRight },
  { keywords: ["transaction", "transactions", "txn", "history"], label: "Transactions", path: "/root/admin/transactions", icon: FileText },
  { keywords: ["agent", "agents"], label: "Agent List", path: "/root/admin/agents/list", icon: Users },
  { keywords: ["create agent", "new agent"], label: "Create Agent", path: "/root/admin/agents/create", icon: Users },
  { keywords: ["kyc", "verification"], label: "KYC Review", path: "/root/admin/kyc", icon: ShieldCheck },
  { keywords: ["bet", "bets"], label: "Bet List", path: "/root/admin/bets/list", icon: Gamepad2 },
  { keywords: ["game", "games"], label: "Games", path: "/root/admin/bets/games", icon: Gamepad2 },
  { keywords: ["report", "reports", "analytics"], label: "Reports & Analytics", path: "/root/admin/reports", icon: FileText },
  { keywords: ["setting", "settings"], label: "Site Settings", path: "/root/admin/settings", icon: ShieldCheck },
  { keywords: ["gateway", "gateways", "payment"], label: "Payment Gateways", path: "/root/admin/gateways", icon: DollarSign },
  { keywords: ["turnover", "rollover"], label: "Turnover Management", path: "/root/admin/turnover", icon: FileText },
  { keywords: ["commission"], label: "Commission", path: "/root/admin/agents/commission", icon: DollarSign },
];

function matchShortcuts(q) {
  const lc = q.toLowerCase().trim();
  if (!lc) return [];
  return PAGE_SHORTCUTS
    .filter((s) => s.keywords.some((k) => k.includes(lc) || lc.includes(k)) || s.label.toLowerCase().includes(lc))
    .slice(0, 5);
}

export default function SmartSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // ⌘K / Ctrl+K to focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Debounced player search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setPlayers([]);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/players", { params: { search: q.trim() } });
        setPlayers((data || []).slice(0, 6));
      } catch {
        setPlayers([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const shortcuts = matchShortcuts(q);
  const results = [
    ...shortcuts.map((s) => ({ kind: "page", data: s })),
    ...players.map((p) => ({ kind: "player", data: p })),
  ];

  const go = (item) => {
    setOpen(false);
    setQ("");
    if (item.kind === "page") {
      navigate(item.data.path);
    } else {
      // Navigate to Players page; stash search query in sessionStorage so the
      // page's DataTable can pre-populate its filter (picked up by Players.js).
      try {
        sessionStorage.setItem("admin-search-q", q.trim());
      } catch { /* ignore */ }
      navigate(`/root/admin/players`);
    }
    setActiveIdx(0);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Enter" && q.trim()) {
        try { sessionStorage.setItem("admin-search-q", q.trim()); } catch { /* ignore */ }
        navigate(`/root/admin/players`);
        setOpen(false);
        setQ("");
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = results[activeIdx];
      if (item) go(item);
    }
  };

  return (
    <div
      ref={wrapRef}
      className="hidden md:flex items-center relative"
      data-testid="smart-search-wrap"
    >
      <div className={`flex items-center gap-2 border rounded-sm px-3 py-1.5 w-[260px] lg:w-[340px] transition-colors ${
        open ? "border-gray-900 bg-white" : "border-gray-200 bg-[#FAFAFA]"
      }`}>
        <Search size={14} className="text-gray-400" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActiveIdx(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className="bg-transparent outline-none text-sm flex-1 placeholder:text-gray-400 min-w-0"
          placeholder="Search players, transactions, agents..."
          data-testid="topbar-search"
          autoComplete="off"
          spellCheck={false}
        />
        {loading ? <Loader2 size={12} className="animate-spin text-gray-400" /> : null}
        <kbd className="text-[10px] font-mono text-gray-400 border border-gray-200 rounded-sm px-1.5 py-0.5 hidden lg:inline-block">⌘K</kbd>
      </div>

      {open && q.trim() && (
        <div
          data-testid="smart-search-dropdown"
          className="absolute top-full left-0 mt-1.5 w-[380px] lg:w-[440px] bg-white border border-gray-200 rounded-sm shadow-lg overflow-hidden z-40"
        >
          {results.length === 0 && !loading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500">
              No matches. Press <kbd className="px-1.5 py-0.5 border border-gray-200 rounded text-[10px] font-mono">Enter</kbd> to search players for "{q.trim()}"
            </div>
          ) : (
            <>
              {shortcuts.length > 0 && (
                <div>
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Jump to</div>
                  {shortcuts.map((s, i) => {
                    const idx = i;
                    const Icon = s.icon || FileText;
                    const active = activeIdx === idx;
                    return (
                      <button
                        key={s.path}
                        type="button"
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => go({ kind: "page", data: s })}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm ${
                          active ? "bg-gray-900 text-white" : "hover:bg-gray-50 text-gray-900"
                        }`}
                        data-testid={`search-shortcut-${s.path.replace(/\//g, "-")}`}
                      >
                        <Icon size={14} className={active ? "text-white" : "text-gray-500"} />
                        <span className="flex-1 font-medium">{s.label}</span>
                        <CornerDownLeft size={12} className={active ? "text-white/70" : "text-gray-400"} />
                      </button>
                    );
                  })}
                </div>
              )}
              {players.length > 0 && (
                <div className={shortcuts.length > 0 ? "border-t border-gray-100" : ""}>
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Players ({players.length})</div>
                  {players.map((p, i) => {
                    const idx = shortcuts.length + i;
                    const active = activeIdx === idx;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => go({ kind: "player", data: p })}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm ${
                          active ? "bg-gray-900 text-white" : "hover:bg-gray-50 text-gray-900"
                        }`}
                        data-testid={`search-player-${p.id}`}
                      >
                        <div className={`h-7 w-7 rounded-sm flex items-center justify-center text-[11px] font-bold shrink-0 ${
                          active ? "bg-white/20 text-white" : "bg-gray-900 text-white"
                        }`}>
                          {(p.name || p.username || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{p.name || p.username}</div>
                          <div className={`text-[11px] truncate ${active ? "text-white/70" : "text-gray-500"}`}>
                            {p.player_id ? `ID ${p.player_id}` : ""}{p.phone ? ` · ${p.phone}` : ""}{p.email ? ` · ${p.email}` : ""}
                          </div>
                        </div>
                        <User size={12} className={active ? "text-white/70" : "text-gray-400"} />
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
          <div className="border-t border-gray-100 px-3 py-2 flex items-center justify-between text-[10px] text-gray-500">
            <span>↑ ↓ navigate · ↵ select · esc close</span>
            <span>⌘K to focus</span>
          </div>
        </div>
      )}
    </div>
  );
}
